import { useEffect, useRef, useState } from "react";

/**
 * Minimal invisible hCaptcha wrapper.
 *
 * Public hCaptcha test keys (docs.hcaptcha.com/#integration-testing):
 *   site key:   10000000-ffff-ffff-ffff-000000000001
 *   secret key: 0x0000000000000000000000000000000000000000
 * They always yield a passing token — safe for preview, must be swapped in production.
 */
const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "10000000-ffff-ffff-ffff-000000000001";
const SDK_SRC = "https://js.hcaptcha.com/1/api.js?render=explicit";

function loadHCaptchaSDK() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
  return new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.hcaptcha || null));
      existing.addEventListener("error", () => resolve(null));
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.hcaptcha || null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}

// One shared pending-resolver so callers can await a token.
const state = { pending: null };

export default function HCaptcha() {
  const nodeRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadHCaptchaSDK().then((sdk) => {
      if (cancelled || !sdk || !nodeRef.current) return;
      try {
        widgetIdRef.current = sdk.render(nodeRef.current, {
          sitekey: SITE_KEY,
          size: "invisible",
          callback: (token) => {
            if (state.pending) { state.pending(token || ""); state.pending = null; }
          },
          "expired-callback": () => {
            if (state.pending) { state.pending(""); state.pending = null; }
          },
          "error-callback": () => {
            if (state.pending) { state.pending(""); state.pending = null; }
          },
        });
        setReady(true);
      } catch {}
    });
    return () => {
      cancelled = true;
      if (state.pending) { state.pending(""); state.pending = null; }
    };
  }, []);

  useEffect(() => {
    window.__rsell_hc = {
      execute: () => {
        if (!ready || widgetIdRef.current == null || !window.hcaptcha) return false;
        try { window.hcaptcha.execute(widgetIdRef.current); return true; } catch { return false; }
      },
      reset: () => {
        if (widgetIdRef.current == null || !window.hcaptcha) return;
        try { window.hcaptcha.reset(widgetIdRef.current); } catch {}
      },
    };
    return () => { delete window.__rsell_hc; };
  }, [ready]);

  return <div ref={nodeRef} data-testid="hcaptcha-widget" style={{ position: "fixed", left: -9999, top: -9999 }} />;
}

// Await a fresh captcha token. Resolves to "" if unavailable or timed out.
export function executeCaptcha() {
  return new Promise(async (resolve) => {
    // Wait up to 8s for the SDK/widget instead of failing the first click.
    const t0 = Date.now();
    while ((!window.__rsell_hc || !window.__rsell_hc.execute) && Date.now() - t0 < 8000) {
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!window.__rsell_hc || !window.__rsell_hc.execute) {
      resolve("");
      return;
    }
    state.pending = resolve;
    let ok = window.__rsell_hc.execute();
    // Widget may exist but not be ready — retry within the same window.
    while (!ok && Date.now() - t0 < 8000) {
      await new Promise((r) => setTimeout(r, 250));
      ok = window.__rsell_hc.execute();
    }
    if (!ok) {
      state.pending = null;
      resolve("");
      return;
    }
    setTimeout(() => {
      if (state.pending === resolve) {
        state.pending = null;
        resolve("");
      }
    }, 20000);
  });
}
